import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Hammer, Wallet, FileText, Search, ClipboardCheck,
  Wrench, PaintBucket, Sparkles, Layers, ShieldCheck, Recycle, Thermometer,
  Home, CheckCircle2, Phone, Calendar, KeyRound
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const RenovationPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const heroImg =
    'https://customer-assets.emergentagent.com/job_construction-global/artifacts/boj91mdi_mb-renov-1.png';

  const steps = [
    { n: '01', Icon: Search, title: t(language, "Visite & diagnostic", 'Site visit & diagnostic'),
      text: t(language,
        "Inspection du bâti existant, relevé des pathologies (fissures, infiltrations, salpêtre, charpente), tests d'humidité et état des réseaux.",
        'Inspection of the existing building, record of pathologies (cracks, leaks, damp, framing), moisture tests and utilities condition.') },
    { n: '02', Icon: ClipboardCheck, title: t(language, "Cahier des charges", 'Scope of works'),
      text: t(language,
        "Définition précise des travaux : rafraîchissement, rénovation lourde ou restructuration. Programme adapté aux usages, au confort et au budget du client.",
        'Precise definition of works: refresh, heavy renovation or restructuring. Programme adapted to client uses, comfort and budget.') },
    { n: '03', Icon: FileText, title: t(language, "Devis détaillé & planning", 'Detailed quote & schedule'),
      text: t(language,
        "Décomposition par lots, métré, choix des matériaux, calendrier prévisionnel et identification des éventuels permis ou déclarations préalables.",
        'Lot-by-lot breakdown, take-off, material selection, provisional schedule and identification of any required permits or prior notifications.') },
    { n: '04', Icon: ShieldCheck, title: t(language, "Autorisations", 'Approvals'),
      text: t(language,
        "Permis de bâtir si modification de structure, surélévation ou changement d'usage. Déclaration auprès de la commune pour les travaux de façade visibles.",
        'Building permit if structural change, addition of a storey or change of use. Notification to the commune for visible façade works.') },
    { n: '05', Icon: Hammer, title: t(language, "Démolition & curage", 'Demolition & strip-out'),
      text: t(language,
        "Déposes sélectives, tri des déchets, mise en sécurité des ouvrages conservés, étaiements provisoires et installation du chantier.",
        'Selective removals, waste sorting, securing of retained elements, temporary shoring and site setup.') },
    { n: '06', Icon: Layers, title: t(language, "Gros œuvre & structure", 'Structure works'),
      text: t(language,
        "Reprise en sous-œuvre, traitement des fissures par injection de résines, renforcement par poutres ou poteaux, étanchéité et reprise de toiture si nécessaire.",
        'Underpinning, crack treatment by resin injection, reinforcement by beams or columns, waterproofing and roof rework if needed.') },
    { n: '07', Icon: Wrench, title: t(language, "Second œuvre & équipements", 'Second fix & equipment'),
      text: t(language,
        "Mise aux normes électriques, plomberie REGIDESO, climatisation, isolation thermique tropicale, menuiseries aluminium, faux plafonds et revêtements.",
        'Electrical compliance, REGIDESO plumbing, air conditioning, tropical thermal insulation, aluminium joinery, suspended ceilings and finishes.') },
    { n: '08', Icon: KeyRound, title: t(language, "Finitions & livraison", 'Finishes & delivery'),
      text: t(language,
        "Peinture, carrelage, parquet, mobilier intégré, nettoyage de fin de chantier, levée des réserves et remise des clés avec dossier des ouvrages exécutés.",
        'Paint, tiling, flooring, built-in furniture, end-of-site cleaning, snagging closure and keys handover with as-built file.') },
  ];

  const prepBlocks = [
    { Icon: Search, title: t(language, "Diagnostic préalable", 'Prior diagnostic'),
      points: [
        t(language, "Inspection structurelle (murs, dalles, charpente, fondations).", 'Structural inspection (walls, slabs, framing, foundations).'),
        t(language, "Audit des réseaux : électricité, plomberie, climatisation.", 'Utilities audit: electrical, plumbing, HVAC.'),
        t(language, "Test d'humidité et de salpêtre, fréquent en saison des pluies.", 'Moisture and salt-damp test, common during the rainy season.'),
        t(language, "Vérification de la présence éventuelle d'amiante ou de plomb.", 'Verification of any asbestos or lead presence.'),
      ] },
    { Icon: Wallet, title: t(language, "Budget & financement", 'Budget & funding'),
      points: [
        t(language, "Comptez en moyenne 30 à 50 % du coût d'un neuf équivalent.", 'Allow on average 30 to 50 % of the cost of an equivalent new build.'),
        t(language, "Distinguer travaux structurels (lourds) et travaux d'embellissement.", 'Distinguish structural (heavy) works from cosmetic works.'),
        t(language, "Provisionner 15–20 % d'aléas, fréquents en rénovation.", 'Set aside 15–20 % contingency, frequent in renovation.'),
        t(language, "Solutions de financement : crédit travaux, paiement échelonné.", 'Funding options: works loan, staged payments.'),
      ] },
    { Icon: FileText, title: t(language, "Cadre légal", 'Legal framework'),
      points: [
        t(language, "Permis de bâtir obligatoire pour modification de structure ou d'usage.", 'Building permit required for structural change or change of use.'),
        t(language, "Déclaration préalable pour les modifications de façade.", 'Prior notification for façade modifications.'),
        t(language, "Respect des règles de mitoyenneté et de copropriété éventuelle.", 'Compliance with party-wall and possible co-ownership rules.'),
        t(language, "Information préalable des voisins en cas de bruit ou poussière.", 'Prior notice to neighbours in case of noise or dust.'),
      ] },
    { Icon: Home, title: t(language, "Choix de l\u2019entreprise", 'Choosing the contractor'),
      points: [
        t(language, "Spécialiste rénovation avec références de chantiers similaires.", 'Renovation specialist with similar project references.'),
        t(language, "Capacité à intervenir en site occupé (logement habité, bureau actif).", 'Ability to operate on occupied site (lived-in home, active office).'),
        t(language, "Devis détaillé, planning ferme, garantie décennale.", 'Detailed quote, firm schedule, decennial warranty.'),
        t(language, "Engagement de propreté et de remise en état des abords.", 'Commitment to cleanliness and restoration of surroundings.'),
      ] },
  ];

  const rdcSpecifics = [
    { Icon: Thermometer, title: t(language, "Climat & humidité", 'Climate & humidity'),
      text: t(language,
        "Le climat tropical de Kinshasa accélère les pathologies (humidité, moisissures, dilatation). Toute rénovation doit intégrer ventilation, isolation tropicale et protection anti-termites.",
        'Kinshasa\u2019s tropical climate accelerates pathologies (damp, mould, expansion). Every renovation should include ventilation, tropical insulation and termite protection.') },
    { Icon: Layers, title: t(language, "Patrimoine bâti", 'Built heritage'),
      text: t(language,
        "De nombreux bâtiments coloniaux ou des années 60–80 nécessitent un traitement particulier : préservation du caractère, mise aux normes structurelles et confort moderne.",
        'Many colonial or 1960s–1980s buildings require specific treatment: preservation of character, structural upgrade and modern comfort.') },
    { Icon: Recycle, title: t(language, "Gestion des déchets", 'Waste management'),
      text: t(language,
        "Tri sélectif des gravats, valorisation possible (concassage pour remblais, réemploi de bois, métaux), évacuation vers les sites autorisés de Kinshasa.",
        'Selective rubble sorting, possible recovery (crushing for backfill, wood and metal reuse), disposal to Kinshasa-approved sites.') },
    { Icon: Sparkles, title: t(language, "Performance énergétique", 'Energy performance'),
      text: t(language,
        "Rénover, c'est aussi l'occasion d'installer climatisation économe, éclairage LED, isolation toiture et préparer une future installation solaire toiture.",
        'Renovating is also the opportunity to install efficient air conditioning, LED lighting, roof insulation and prepare a future rooftop solar installation.') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="renovation-page">
        <section className="relative h-[360px] md:h-[460px] overflow-hidden" data-testid="renovation-hero">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button onClick={() => navigate('/')} className="self-start inline-flex items-center gap-2 text-white/85 hover:text-orange-400 mb-6 transition-colors" data-testid="renovation-back-btn">
              <ArrowLeft size={18} />
              <span className="font-semibold">{t(language, "Retour à l'accueil", 'Back to home')}</span>
            </button>
            <h1 className="text-white text-3xl sm:text-4xl md:text-6xl font-bold leading-tight max-w-3xl">
              {t(language, "Rénover votre maison ou votre bâtiment", 'Renovate your house or building')}
            </h1>
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(language,
                "Du rafraîchissement à la restructuration complète : redonnez vie à votre bien à Kinshasa.",
                'From refresh to full restructuring: bring your property in Kinshasa back to life.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="renovation-intro">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-6" style={{ color: '#702963' }}>
              {t(language,
                "Rénover, c'est valoriser durablement votre bien",
                'Renovating means lasting value for your property')}
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t(language,
                "Qu'il s'agisse d'une villa familiale, d'un immeuble d'habitation, de bureaux ou d'un local commercial, la rénovation permet de remettre aux normes, gagner en confort, optimiser la performance énergétique et accroître la valeur du patrimoine. Pour la rénovation, Global Construction et Ingégnerie impose une démarche structurée : diagnostic rigoureux, choix de matériaux adaptés au climat et coordination fine pour limiter les nuisances en site occupé.",
                'Whether it is a family villa, a residential block, offices or a commercial premises, renovation upgrades to standards, improves comfort, optimises energy performance and increases asset value. For its renovation projects, Global Construction & Ingégnerie applies a structured approach: rigorous diagnostic, climate-adapted materials and careful coordination to limit disruption on occupied sites.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gray-50" data-testid="renovation-prep">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Préparation client : 4 piliers", 'Client preparation: 4 pillars')}
              </h2>
              <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {prepBlocks.map((b, idx) => {
                const Icon = b.Icon;
                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-sm p-7 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`renovation-prep-${idx}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 flex-shrink-0">
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">{b.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {b.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700 text-sm md:text-base leading-relaxed">
                          <CheckCircle2 className="w-5 h-5 text-[#702963] flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24" data-testid="renovation-steps">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Les 8 étapes d'une rénovation maîtrisée", 'The 8 steps of a controlled renovation')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />
              <div className="space-y-10 md:space-y-16">
                {steps.map((s, i) => {
                  const Icon = s.Icon;
                  const left = i % 2 === 0;
                  return (
                    <div key={s.n} className="md:grid md:grid-cols-2 md:gap-12 items-center" data-testid={`renovation-step-${s.n}`}>
                      <div className={`${left ? 'md:order-1 md:text-right' : 'md:order-2'} mb-4 md:mb-0`}>
                        <div className={`flex items-start gap-4 ${left ? 'md:flex-row-reverse md:text-right' : ''}`}>
                          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#702963] text-white rounded-full flex-shrink-0">
                            <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2} />
                          </div>
                          <div>
                            <span className="text-orange-500 font-bold text-sm tracking-widest">{t(language, 'ÉTAPE', 'STEP')} {s.n}</span>
                            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mt-1 mb-2">{s.title}</h3>
                            <p className="text-gray-700 text-sm md:text-base leading-relaxed">{s.text}</p>
                          </div>
                        </div>
                      </div>
                      <div className={`hidden md:block ${left ? 'md:order-2' : 'md:order-1'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-gray-50" data-testid="renovation-rdc">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Spécificités à Kinshasa", 'Kinshasa specifics')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {rdcSpecifics.map((r, i) => {
                const Icon = r.Icon;
                return (
                  <div key={i} className="bg-white p-6 md:p-7 border-t-4 border-[#702963] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`renovation-rdc-card-${i}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 mb-4">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{r.title}</h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="renovation-duration">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-[#702963] text-white p-8 md:p-12 rounded-sm shadow-2xl">
              <div className="flex items-start gap-5">
                <Calendar className="w-10 h-10 text-orange-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-3">{t(language, 'Durée indicative', 'Indicative duration')}</h3>
                  <p className="text-white/90 text-base md:text-lg leading-relaxed">
                    {t(language,
                      "Pour un rafraîchissement complet d'une maison à Kinshasa, comptez 6 à 12 semaines. Pour une rénovation lourde (structure, réseaux, second œuvre), prévoyez 4 à 8 mois. Pour une restructuration totale ou une surélévation, la durée peut atteindre 10 à 14 mois.",
                      'For a full refresh of a house in Kinshasa, allow 6 to 12 weeks. For heavy renovation (structure, utilities, second fix), allow 4 to 8 months. For full restructuring or adding a storey, expect 10 to 14 months.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white" data-testid="renovation-cta">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#702963' }}>
              {t(language, 'Un projet de rénovation ?', 'A renovation project?')}
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              {t(language,
                "Diagnostic gratuit sur site et devis détaillé sous 7 jours ouvrés.",
                'Free on-site diagnostic and detailed quote within 7 business days.')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/votre-projet')} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 transform hover:scale-105" data-testid="renovation-cta-project">
                {t(language, 'Demander un diagnostic', 'Request a diagnostic')}
                <ArrowRight size={18} />
              </button>
              <a href="tel:+243971327318" className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-8 py-3 hover:bg-[#702963] hover:text-white transition-all" data-testid="renovation-cta-call">
                <Phone size={18} />
                {t(language, 'Appelez-nous', 'Call us')}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RenovationPage;
